import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  model,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiTagComponent, UiTagColor } from '../tag/tag.component';

let uiSelectUid = 0;

export interface UiSelectOption {
  id: string | number;
  label: string;
  value?: string | number | null;
  disabled?: boolean;
  /** Si se define, la opción se muestra como `ui-tag` outline. */
  color?: UiTagColor;
}

interface PanelRect {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
}

/**
 * Átomo: select con búsqueda interna, badge obligatorio/opcional y botón limpiar.
 * El panel usa position:fixed calculado desde el trigger para escapar
 * cualquier overflow:hidden en la jerarquía del DOM. Si no cabe abajo,
 * se ancla hacia arriba.
 */
@Component({
  selector: 'ui-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, UiTagComponent],
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  host: {
    class: 'ui-select-host',
    '[class.ui-select-host--block]': 'block()',
    '[class.ui-select-host--fit-panel]': 'fitPanelToContent()',
  },
})
export class UiSelectComponent {
  @ViewChild('triggerBtn') private triggerBtn!: ElementRef<HTMLButtonElement>;
  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  readonly label = input('');
  readonly options = input<UiSelectOption[]>([]);
  readonly selected = model<UiSelectOption | null>(null);
  readonly placeholder = input('Selecciona una opción');

  readonly required = input(false);
  readonly showOptionalHint = input(true);
  readonly requiredHint = input('Obligatorio');
  readonly optionalHint = input('Opcional');

  readonly disabled = input(false);
  readonly invalid = input(false);
  readonly message = input<string | undefined>(undefined);
  readonly showMessage = input(true);
  readonly searchPlaceholder = input('Buscar...');

  readonly showLabel = input(true);
  readonly showSearch = input(true);
  readonly allowClear = input(true);
  readonly block = input(false);
  /**
   * Si es true, el panel usa el ancho del contenido (mínimo el del trigger)
   * en lugar de igualar el ancho del trigger. Útil en columnas angostas.
   */
  readonly fitPanelToContent = input(false);
  readonly fieldId = input<string | undefined>(undefined);

  readonly selectedChange = output<UiSelectOption | null>();
  readonly opened = output<void>();
  readonly closed = output<void>();

  protected readonly generatedId = `ui-select-${++uiSelectUid}`;
  protected readonly triggerId = computed(() => this.fieldId() ?? this.generatedId);
  protected readonly listboxId = computed(() => `${this.triggerId()}-listbox`);
  protected readonly messageId = computed(() => `${this.triggerId()}-message`);

  protected readonly isOpen = signal(false);
  protected readonly searchQuery = signal('');

  /** Coordenadas del panel (position: fixed). */
  protected readonly panelRect = signal<PanelRect | null>(null);

  protected readonly showRequirement = computed(
    () => this.required() || this.showOptionalHint(),
  );

  protected readonly requirementText = computed(() =>
    this.required() ? this.requiredHint() : this.optionalHint(),
  );

  protected readonly ariaDescribedBy = computed(() =>
    this.message() ? this.messageId() : null,
  );

  protected readonly filteredOptions = computed(() => {
    const q = this.searchQuery().toLowerCase().trim();
    if (!q) return this.options();
    return this.options().filter((o) => o.label.toLowerCase().includes(q));
  });

  /** Índice de la opción activa durante la navegación por teclado (-1 = ninguna). */
  protected readonly activeIndex = signal(-1);

  /** ID DOM de la opción actualmente activa (para aria-activedescendant). */
  protected readonly activeOptionId = computed(() => {
    const idx = this.activeIndex();
    if (idx < 0) return null;
    return `${this.listboxId()}-opt-${idx}`;
  });

  /** Devuelve el ID DOM de la opción en la posición dada. */
  protected optionId(index: number): string {
    return `${this.listboxId()}-opt-${index}`;
  }

  constructor(private readonly elRef: ElementRef<HTMLElement>) {
    const onScroll = (event: Event) => {
      if (!this.isOpen()) return;
      const panel = document.getElementById(this.listboxId());
      if (panel?.contains(event.target as Node)) return;
      this.close();
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    inject(DestroyRef).onDestroy(() =>
      document.removeEventListener('scroll', onScroll, { capture: true }),
    );
  }

  /** Cierra al hacer click fuera del host Y fuera del panel (que está en fixed). */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    const panel = document.getElementById(this.listboxId());
    const insideHost = this.elRef.nativeElement.contains(target);
    const insidePanel = panel?.contains(target) ?? false;
    if (!insideHost && !insidePanel) {
      this.close();
    }
  }

  /** Cierra con Escape y devuelve el foco al trigger. */
  @HostListener('keydown.escape')
  onEscape(): void {
    this.close(true);
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isOpen()) this.close(false);
  }

  protected toggle(): void {
    if (this.disabled()) return;
    this.isOpen() ? this.close(true) : this.open();
  }

  protected open(): void {
    const rect = this.triggerBtn.nativeElement.getBoundingClientRect();
    const gap = 4;
    const searchH = this.showSearch() ? 49 : 0;
    const listMax = 14 * 16;
    const panelH = searchH + listMax + 2;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openAbove = spaceBelow < panelH && spaceAbove > spaceBelow;

    this.panelRect.set(
      openAbove
        ? { bottom: window.innerHeight - rect.top + gap, left: rect.left, width: rect.width }
        : { top: rect.bottom + gap, left: rect.left, width: rect.width },
    );
    this.isOpen.set(true);
    this.searchQuery.set('');
    // Pre-selecciona el índice de la opción ya elegida
    const selIdx = this.options().findIndex((o) => o.id === this.selected()?.id);
    this.activeIndex.set(selIdx >= 0 ? selIdx : -1);
    this.opened.emit();
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
  }

  /**
   * @param refocus – true para devolver el foco al trigger (Esc / selección).
   *                  false cuando el cierre es por Tab o resize (foco natural).
   */
  protected close(refocus = false): void {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
    this.panelRect.set(null);
    this.activeIndex.set(-1);
    this.closed.emit();
    if (refocus) {
      setTimeout(() => this.triggerBtn?.nativeElement?.focus(), 0);
    }
  }

  protected select(option: UiSelectOption): void {
    if (option.disabled) return;
    this.selected.set(option);
    this.selectedChange.emit(option);
    this.close(true);
  }

  protected clear(event: Event): void {
    event.stopPropagation();
    this.selected.set(null);
    this.selectedChange.emit(null);
  }

  protected onSearch(query: string): void {
    this.searchQuery.set(query);
    this.activeIndex.set(-1);
  }

  protected isSelected(option: UiSelectOption): boolean {
    return this.selected()?.id === option.id;
  }

  // ── Teclado ───────────────────────────────────────────────────────────────

  /** Manejador de teclas en el botón trigger. */
  protected onTriggerKeydown(event: KeyboardEvent): void {
    if (this.disabled()) return;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen()) {
          this.open();
        } else if (!this.showSearch()) {
          this.moveActiveIndex(event.key === 'ArrowDown' ? 1 : -1);
        }
        break;
      case 'Enter':
        if (!this.isOpen()) {
          event.preventDefault();
          this.open();
        } else if (!this.showSearch()) {
          event.preventDefault();
          this.selectActive();
        }
        break;
      case ' ':
        if (!this.isOpen()) {
          event.preventDefault();
          this.open();
        }
        break;
      case 'Tab':
        if (this.isOpen()) this.close(false);
        break;
    }
  }

  /** Manejador de teclas en el input de búsqueda. */
  protected onSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActiveIndex(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActiveIndex(-1);
        break;
      case 'Enter':
        event.preventDefault();
        this.selectActive();
        break;
      case 'Tab':
        this.close(false);
        break;
    }
  }

  /** Selecciona la opción marcada como activa. */
  private selectActive(): void {
    const idx = this.activeIndex();
    const opt = this.filteredOptions()[idx];
    if (opt) this.select(opt);
  }

  /** Mueve el índice activo en `delta` pasos, saltando opciones deshabilitadas. */
  private moveActiveIndex(delta: number): void {
    const opts = this.filteredOptions();
    if (!opts.length) return;
    let next = this.activeIndex() + delta;
    let attempts = 0;
    while (next >= 0 && next < opts.length && opts[next].disabled && attempts < opts.length) {
      next += delta;
      attempts++;
    }
    next = Math.max(0, Math.min(next, opts.length - 1));
    if (opts[next]?.disabled) return;
    this.activeIndex.set(next);
    setTimeout(() => {
      document.getElementById(this.optionId(next))?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }
}
